// The sidebar (canvas 4b · Machine, compact): the wordmark and hide, the sessions as two-line rows (flat, by program,
// or by folder as a tree), then the machine's pages (Desktop, Files, Settings) and you at the foot. Each row on the
// desktop is a workspace (tabs.ts): picking it shows its panes as they were left, and its mark is the most urgent of
// its sessions'; a session added to a row as a tab, or split off it, has no row of its own. A row is a plain band
// while its panes show; hover shows Rename and End.

import {
  IconActivity,
  IconArrowDown,
  IconArrowUp,
  IconArrowUpRight,
  IconBook,
  IconDownload,
  IconBrandNodejs,
  IconBrandOpenai,
  IconCheck,
  IconChevronDown,
  IconFolder,
  IconDeviceDesktop,
  IconDots,
  IconFilter2,
  IconLayoutDashboard,
  IconLayoutOff,
  IconLayoutSidebarLeftCollapse,
  IconLogout,
  IconPencil,
  IconPin,
  IconPinFilled,
  IconPinnedOff,
  IconPlus,
  IconSettings,
  IconTerminal2,
  IconX,
  type Icon as TablerIcon,
} from '@tabler/icons-react';
import { Fragment, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import type { Session } from '../net/protocol';
import { useStore } from '../store';
import { IconBrandClaude } from './BrandIcons';
import { Button, Empty, Ic, Kbd, Mark, Mono, Pix, Tool, Tooltip, presence } from './ds';
import { menuKeys, useMenu } from './menu';
import { RenameDialog } from './RenameDialog';
import { escCloses, isMac, numModLabel } from './keys';
import { LoadBanner } from './LoadBanner';
import { reducedMotion, useListMotion, type Shown } from './motion';
import { groupSessions, pinnedFirst, setOrganize, useListOrder, useOrganize, type Organize, type SessionGroup } from './organize';
import { isPinned, prunePins, togglePin, usePins } from './pins';
import { moveRows, pruneOrder, useOrder } from './rowOrder';
import { sessionPlace } from './sessionMeta';
import { WAITING_LINE, waitingAsk } from './waiting';
import { ago, secondsSince, stateText, stateWords, useNow } from './time';
import { regionCity } from './Machine';
import { statusMark } from './PaneBar';
import { current as shownGroup, focusedSession, groupOf, inner, joinRow, MAX_PANES, members, paneOrder, rowStatus, type Layout, type TabsState } from './tabs';
import { panesAspect, useActions } from './Workspace';

/** A row's class for its part in the list's motion: arriving, leaving (ui/motion.ts). */
const rowPhase = (phase: Shown<unknown>['phase']) => (phase === 'enter' ? 'enter' : phase === 'leave' ? 'leave' : undefined);

const PROGRAM_ICON: Record<string, TablerIcon> = { Codex: IconBrandOpenai, node: IconBrandNodejs, htop: IconActivity };
/** A program's mark in its group's header: the agent's own, else a terminal. */
function ProgramIcon({ program }: { program?: string }) {
  if (program === 'Claude Code' || program === 'claude') return <IconBrandClaude size={14} aria-hidden />;
  return <Ic icon={(program && PROGRAM_ICON[program]) || IconTerminal2} size={14} />;
}

/** The row's second line: what its group's header doesn't already say; "Waiting on you" while it needs you. */
function subLine(s: Pick<Session, 'row'> & Partial<Pick<Session, 'status' | 'notice' | 'exitCode'>>, organize: Organize) {
  if (s.status === 'waiting') return WAITING_LINE; // what it asked is the row's tooltip
  const { program, where, branch } = s.row?.meta[organize] ?? {};
  const parts = [program, where, branch];
  if (s.status === 'exited') parts.splice(1, 2, `exit ${s.exitCode ?? 0}`);
  return parts.filter(Boolean).join(' · ');
}

/** Ending asks first only when something runs in it: a shell at its prompt, or one whose program exited, just goes. */
const asks = (s: Session) => !!s.program && s.status !== 'exited';

/** "2m": how long its state has lasted, on the machine's clock (the same rule as every row: ui/time.ts); nothing while it works. */
function since(s: Session, now: number) {
  const secs = secondsSince(stateWords(s).since, now);
  return Number.isFinite(secs) ? ago(secs) : '';
}

function SessionRow({ s, index, current, organize, phase, tree, status = s.status, reveal, drag, drop, onLift, unsplit, move }: { s: Session; index: number; current: boolean; organize: Organize; phase: Shown<unknown>['phase']; tree?: LegShape; status?: Session['status']; reveal?: boolean; drag?: RowDrag; drop?: Slot['edge']; onLift?: (e: ReactPointerEvent<HTMLElement>) => void; unsplit?: () => void; move?: RowMove }) {
  const a = useActions();
  const now = useNow();
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const menu = useMenu();
  const pinned = usePins().includes(s.id);

  // the dialog closes: focus goes back to the ⋯ that opened it
  const closeRename = () => {
    // focus first, while the row still shows its actions
    menu.ref.current?.querySelector<HTMLElement>('[aria-haspopup]')?.focus();
    setEditing(false);
  };
  // No name: the session names itself again (its agent's title, an AI name, its folder);
  // agents that predate naming leave nameSource out and would keep an empty name.
  const automatic = s.nameSource === 'user';
  // only what the row doesn't say: what it asked (waiting), what runs in it, where
  // (the place, "Claude Code · ~/app · main", already says the program and the folder)
  const tip = [
    s.status === 'waiting' && waitingAsk(s),
    sessionPlace(s) || [s.title || s.command || s.name, s.cwd].filter(Boolean).join('\n'),
  ]
    .filter(Boolean)
    .join('\n');
  const waiting = s.status === 'waiting';
  const mark = (
    <>
      <Mark {...statusMark(status)} />
      {pinned && (
        <span className="ws-sb-pin" data-testid="pin-mark" role="img" aria-label="Pinned">
          <Ic icon={IconPinFilled} size={12} />
        </span>
      )}
    </>
  );
  const sub = subLine(s, organize);
  // a row dragged onto: its sub-line gives way to what letting go does
  const hint = drag === 'armed' ? 'Release to split' : drag === 'full' ? '4 panes already' : '';
  const when = since(s, now);

  let body: ReactNode;
  if (confirming) {
    body = (
      <div className="ws-sb-confirm">
        <span className="ws-sb-line">
          {mark}
          <span className="ws-sb-title" data-testid="session-name">
            {s.name}
          </span>
        </span>
        <span className="ws-sb-ask" role="group" aria-label={`End ${s.name}?`} onKeyDown={escCloses(() => setConfirming(false))}>
          <span className="ws-sb-ask-q">End this session?</span>
          <Button
            size="xs"
            tone="danger"
            radius={6}
            autoFocus
            data-testid="kill-confirm"
            onClick={() => {
              setConfirming(false);
              void a.kill(s.id);
            }}
          >
            End
          </Button>
          <Button size="xs" radius={6} data-testid="kill-cancel" onClick={() => setConfirming(false)}>
            Cancel
          </Button>
        </span>
      </div>
    );
  } else {
    body = (
      <>
        <button type="button" className="ws-sb-main" aria-current={current ? 'true' : undefined} title={tip} onClick={() => (reveal ? a.revealSession(s.id) : a.selectSession(s.id))} onDoubleClick={() => setEditing(true)}>
          <span className="ws-sb-line">
            {mark}
            <span className="ws-sb-title" data-testid="session-name">
              {s.name}
            </span>
            <span className="ws-sb-when">
              {when && <span className="ws-sb-ago">{when}</span>}
              {index < 9 && (
                <span className="ws-sb-key" aria-hidden="true">
                  {numModLabel()}
                  {index + 1}
                </span>
              )}
            </span>
          </span>
          {(sub || hint) && (
            <span className="ws-sb-subs">
              {sub && (
                <span className="ws-sb-sub" data-testid="session-meta" data-tone={waiting ? 'attn' : undefined}>
                  {sub}
                </span>
              )}
              {hint && (
                <span className="ws-sb-sub ws-sb-hint" data-testid="drop-hint" data-tone={drag}>
                  {hint}
                </span>
              )}
            </span>
          )}
          {/* what the row says in words, the same on every platform ("Needs you · 5m"); the mark and the age carry it to the eye */}
          <span className="ds-sr" data-testid="session-state">
            {stateText(stateWords(s), now)}
          </span>
        </button>
        <span className="ws-sb-acts">
          <span className="ws-menu-wrap" ref={menu.ref} data-open={menu.open ? '' : undefined}>
            <Tool
              icon={IconDots}
              iconSize={14}
              size="xs"
              label="Session options"
              align="end"
              aria-haspopup="menu"
              aria-expanded={menu.open}
              data-testid="session-options"
              onClick={() => menu.setOpen((o) => !o)}
            />
            {menu.p.mounted && (
              <div
                className="ws-menu ds-float ws-sb-menu"
                role="menu"
                aria-label={`Options for ${s.name}`}
                data-flip={menu.flip ? '' : undefined}
                {...presence(menu.p)}
                onKeyDown={menuKeys}
              >
                <button
                  type="button"
                  className="ws-menu-item"
                  role="menuitem"
                  data-testid="rename"
                  onClick={() => {
                    menu.setOpen(false);
                    setEditing(true);
                  }}
                >
                  <span className="ws-menu-icon">
                    <Ic icon={IconPencil} size={16} />
                  </span>
                  Rename session…
                </button>
                <button
                  type="button"
                  className="ws-menu-item"
                  role="menuitem"
                  data-testid="pin"
                  onClick={() => {
                    menu.setOpen(false);
                    togglePin(s.id);
                  }}
                >
                  <span className="ws-menu-icon">
                    <Ic icon={pinned ? IconPinnedOff : IconPin} size={16} />
                  </span>
                  {pinned ? 'Unpin session' : 'Pin session'}
                </button>
                {move?.up && (
                  <button
                    type="button"
                    className="ws-menu-item"
                    role="menuitem"
                    data-testid="move-up"
                    onClick={() => {
                      menu.setOpen(false);
                      move.up?.();
                    }}
                  >
                    <span className="ws-menu-icon">
                      <Ic icon={IconArrowUp} size={16} />
                    </span>
                    Move up
                  </button>
                )}
                {move?.down && (
                  <button
                    type="button"
                    className="ws-menu-item"
                    role="menuitem"
                    data-testid="move-down"
                    onClick={() => {
                      menu.setOpen(false);
                      move.down?.();
                    }}
                  >
                    <span className="ws-menu-icon">
                      <Ic icon={IconArrowDown} size={16} />
                    </span>
                    Move down
                  </button>
                )}
                {unsplit && (
                  <button
                    type="button"
                    className="ws-menu-item"
                    role="menuitem"
                    data-testid="unsplit"
                    onClick={() => {
                      menu.setOpen(false);
                      unsplit();
                    }}
                  >
                    <span className="ws-menu-icon">
                      <Ic icon={IconLayoutOff} size={16} />
                    </span>
                    Remove from split view
                  </button>
                )}
              </div>
            )}
          </span>
          <Tool icon={IconX} iconSize={14} size="xs" label="End session" align="end" aria-label={`End ${s.name}`} data-testid="kill" onClick={() => (asks(s) ? setConfirming(true) : void a.kill(s.id))} />
        </span>
      </>
    );
  }

  return (
    <li
      className="ws-sb-row"
      data-key={s.id}
      data-phase={rowPhase(phase)}
      data-current={current ? '' : undefined}
      data-mode={confirming ? 'confirm' : undefined}
      data-menu={menu.open || editing ? '' : undefined}
      data-pinned={pinned ? '' : undefined}
      data-idle={s.status === 'idle' || s.status === 'exited' ? '' : undefined}
      data-testid="session-row"
      data-session-id={s.id}
      data-session-name={s.name}
      data-name-source={s.nameSource ?? ''}
      data-status={s.status}
      data-drag={drag}
      data-drop={drop}
      onPointerDown={onLift}
    >
      {tree && <Leg {...tree} />}
      <div className="ws-sb-body">{body}</div>
      <RenameDialog open={editing} name={s.name} automatic={automatic} onSave={(name) => void a.rename(s.id, name)} onClose={closeRename} />
    </li>
  );
}

/** A row's tree leg: the line down from the group's mark, an elbow into the last row. */
type LegShape = { first: boolean; last: boolean; from?: number };
const Leg = ({ first, last, from = -7 }: LegShape) => (
  <svg className="ws-sb-leg" width="20" height="50" viewBox="0 0 20 50" aria-hidden="true">
    <path d={last ? `M7.5 ${first ? from : 0}V13a4 4 0 0 0 4 4H18` : `M7.5 ${first ? from : 0}V50M7.5 17H18`} />
  </svg>
);

/** How long ⌘ is held before the hints show: a ⌘C or ⌘T chord is over by then and never flashes them. */
const KBD_HINT_DELAY_MS = 900;

/** Holding ⌘ (Alt elsewhere) alone for KBD_HINT_DELAY_MS shows each row's ⌘1–9 in place of its time. Releasing hides
 *  them at once; any other key pressed while it is down cancels the wait, and another modifier with it (⌘⇧4, the
 *  system's screenshot, whose 4 never reaches the page) means no hints for the rest of that hold. */
function useKbdHeld() {
  useEffect(() => {
    const root = document.documentElement;
    const key = isMac() ? 'Meta' : 'Alt';
    let timer = 0;
    const set = (on: boolean) => root.classList.toggle('kbd-held', on);
    const down = (e: globalThis.KeyboardEvent) => {
      if (e.key === key) {
        // a repeat is the same hold: it doesn't restart the wait, nor begin one after a chord
        if (e.repeat) return;
        clearTimeout(timer);
        const others = e.shiftKey || e.ctrlKey || (key === 'Meta' ? e.altKey : e.metaKey);
        if (!others) timer = window.setTimeout(() => set(true), KBD_HINT_DELAY_MS);
      } else {
        clearTimeout(timer);
        if (['Meta', 'Alt', 'Control', 'Shift'].includes(e.key)) set(false);
      }
    };
    const up = (e: globalThis.KeyboardEvent) => {
      if (e.key !== key) return;
      clearTimeout(timer);
      set(false);
    };
    const off = () => {
      clearTimeout(timer);
      set(false);
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', off);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', off);
      off();
    };
  }, []);
}

/** Rows that glide to their new places as the list reorders (ui/motion.ts). index: each one's ⌘1–9 place. */
function SessionRows({ sessions, index, organize, current, testid, tree, statusOf, reveal, dragOf, dock, liftOf, unsplit }: { sessions: Session[]; index: Map<number, number>; organize: Organize; current?: number; testid?: string; tree?: boolean; statusOf?: (s: Session) => Session['status']; reveal?: boolean; dragOf?: (s: Session) => RowDrag | undefined; dock?: ReactNode; liftOf?: (e: ReactPointerEvent<HTMLElement>, s: Session) => void; unsplit?: boolean }) {
  const a = useActions();
  const { ref, shown } = useListMotion<Session, HTMLUListElement>(sessions, (s) => s.id);
  return (
    <ul className="ws-sb-list" ref={ref} data-testid={testid} data-organize={testid ? organize : undefined}>
      {shown.map(({ item: s, key, phase }, i) => (
        <SessionRow
          key={key}
          s={s}
          index={index.get(s.id) ?? 99}
          organize={organize}
          current={current === s.id}
          phase={phase}
          status={statusOf?.(s)}
          reveal={reveal}
          drag={dragOf?.(s)}
          onLift={liftOf && phase !== 'leave' ? (e) => liftOf(e, s) : undefined}
          unsplit={unsplit ? () => a.unsplit(s.id) : undefined}
          // a dock under the last row: its elbow carries on as a straight leg down to the dock's
          tree={tree ? { first: i === 0, last: i === shown.length - 1 && !dock } : undefined}
        />
      ))}
      {dock}
    </ul>
  );
}

type Workspace = { kind: 'row'; s: Session } | { kind: 'group'; lead: Session; members: Session[] };

/**
 * The desktop list (cmux's workspaces): a row's workspace holding one session is that row; one holding more (split
 * panes, tabs added with +) is a group where its row was, its folder and name (the title bar's: double-click renames
 * it), and under it every session it holds on tree legs, in pane order and then tab order. The header (and ⌘1–9)
 * shows the workspace as it was left; a session's row there shows it with that pane and tab in front.
 */
function WorkspaceRows({ sessions, index, current, focused, statusOf }: { sessions: Session[]; index: Map<number, number>; current?: number; focused?: number; statusOf?: (s: Session) => Session['status'] }) {
  const a = useActions();
  const all = useStore((st) => st.sessions);
  const items: Workspace[] = sessions.map((s) => {
    const g = groupOf(a.tabs, s.id);
    const ids = g && g.lead === s.id ? members(g) : [];
    const held = ids.map((id) => all.find((x) => x.id === id)).filter((x): x is Session => !!x);
    return held.length > 1 ? { kind: 'group', lead: s, members: held } : { kind: 'row', s };
  });
  const { ref, shown } = useListMotion<Workspace, HTMLUListElement>(items, (it) => (it.kind === 'row' ? it.s.id : `g${it.lead.id}`));
  const { view, lift, ghost } = useRowDrag(ref, a.tabs, a.compact, a.joinRow, a.unsplit, (ids, before) => moveRows(all, ids, before));
  const aimed = view?.aim;
  // where it would move to: a line in that gap, until resting on a row arms the split
  const dropOf = (lead: number) => (view?.slot && !view.armed && view.slot.on === lead ? view.slot.edge : undefined);
  const leadOf = (it: Workspace) => (it.kind === 'row' ? it.s.id : it.lead.id);
  const at = new Map(items.map((it, i) => [leadOf(it), i]));
  const [said, setSaid] = useState('');
  /** One place up or down among its own (a pinned one among the pinned), without dragging: its menu, or Alt+Shift+↑/↓
   *  on it (a tab's Alt+Shift+←/→). Undefined at the end. */
  const step = (lead: number, by: -1 | 1) => {
    const i = at.get(lead);
    const it = i === undefined ? undefined : items[i];
    const to = i === undefined ? undefined : items[i + by];
    if (!it || !to || isPinned(lead) !== isPinned(leadOf(to))) return undefined;
    const after = by < 0 ? to : items[i! + 2];
    return () => {
      moveRows(all, it.kind === 'row' ? [lead] : [lead, ...it.members.map((m) => m.id).filter((id) => id !== lead)], after ? leadOf(after) : null);
      setSaid(`${it.kind === 'row' ? it.s.name : it.lead.name} moved ${by < 0 ? 'up' : 'down'}, ${i! + by + 1} of ${items.length}`);
    };
  };
  const onKeys = (e: ReactKeyboardEvent<HTMLUListElement>) => {
    if (!e.altKey || !e.shiftKey || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') || (e.target as Element).closest('dialog, input, textarea')) return;
    const top = [...e.currentTarget.children].find((c) => c.contains(e.target as Node)) as HTMLElement | undefined;
    const go = top && step(Number(top.dataset.lead ?? top.dataset.sessionId), e.key === 'ArrowUp' ? -1 : 1);
    if (!go) return;
    e.preventDefault();
    go();
  };
  // armed: the group it would make, so the dock draws its layout and where the dragged session goes in it
  const made = view?.armed && aimed && !aimed.out ? joinRow(a.tabs, view.sid, aimed.lead, panesAspect()) : null;
  const into = made?.groups.find((g) => g.id === made.at);
  const dockFor = (lead: number, lone?: boolean) =>
    into && aimed?.lead === lead ? <SplitDock name={view!.name} layout={into.layout} pane={into.panes.length - 1} lone={lone} /> : null;
  const dragOf = (s: Session): RowDrag | undefined =>
    view?.kind === 'row' && view.sid === s.id ? 'source' : aimed?.key === `r${s.id}` ? (view!.armed ? 'armed' : aimed.full ? 'full' : undefined) : undefined;
  const leadName = aimed && all.find((x) => x.id === aimed.lead)?.name;
  const slotName = view?.slot && !view.armed ? (view.slot.before === null ? '' : (all.find((x) => x.id === view.slot!.before)?.name ?? '')) : undefined;
  return (
    <>
      <ul className="ws-sb-list" ref={ref} data-testid="session-list" data-organize="none" onKeyDown={onKeys}>
        {shown.map(({ item: it, key, phase }) =>
          it.kind === 'row' ? (
            <Fragment key={key}>
              <SessionRow
                s={it.s}
                index={index.get(it.s.id) ?? 99}
                organize="none"
                current={current === it.s.id}
                phase={phase}
                status={statusOf?.(it.s)}
                drag={dragOf(it.s)}
                drop={dropOf(it.s.id)}
                onLift={phase === 'leave' ? undefined : (e) => lift(e, it.s)}
                move={phase === 'leave' ? undefined : { up: step(it.s.id, -1), down: step(it.s.id, 1) }}
              />
              {dockFor(it.s.id, true)}
            </Fragment>
          ) : (
            <li key={key} data-key={key} className="ws-sb-group" data-phase={rowPhase(phase)} data-testid="session-group" data-group={it.lead.name} data-lead={it.lead.id} data-split="" data-drag={view?.kind === 'group' && view.sid === it.lead.id ? 'source' : undefined} data-drop={dropOf(it.lead.id)}>
              <GroupHead lead={it.lead} index={index.get(it.lead.id) ?? 99} current={current === it.lead.id} armed={!!view?.armed && aimed?.key === `h${it.lead.id}`} onLift={phase === 'leave' ? undefined : (e) => lift(e, it.lead, null, 'group')} />
              <SessionRows sessions={it.members} index={NO_KEYS} organize="none" current={current === it.lead.id ? focused : undefined} reveal tree dragOf={dragOf} dock={dockFor(it.lead.id)} liftOf={(e, s) => lift(e, s, it.lead.id)} unsplit />
            </li>
          ),
        )}
      </ul>
      {ghost}
      <span className="ds-sr" aria-live="polite">
        {view?.armed && aimed?.out
          ? `Release to take ${view.name} out of the split`
          : view?.armed && leadName
            ? `Release to split ${view.name} with ${leadName}`
            : view && slotName !== undefined
              ? `Release to move ${view.name} ${slotName ? `above ${slotName}` : 'to the end'}`
              : said}
      </span>
    </>
  );
}

/** A row being dragged (`source`), or one dragged onto: armed to split, or already at 4 panes (`full`). */
type RowDrag = 'source' | 'armed' | 'full';

/** A row's Move up and Move down (its ⋯ menu); undefined where it can't go. */
type RowMove = { up?: () => void; down?: () => void };

/** How long the pointer rests on a row before it arms: long enough that passing over rows on the way arms none of them,
 *  short enough to feel immediate once people know the gesture (Arc's is well under a second). Nothing shows the wait. */
const ARM_DELAY_MS = 150;
/** How far the pointer goes before a press is a drag, not a click. */
const DRAG_SLOP = 4;
/** A cancelled drag's ghost slides back to its row and fades (workspace.css .ws-sb-ghost[data-back]). */
const GHOST_BACK_MS = 220;
/** How near a gap the pointer is for a row to move there: a row's nearer half, a group's first and last 26px (the
 *  rest of a group is for joining it). */
const SLOT_REACH = 26;
/** A row's (or a group's) top and bottom, where resting never arms a split: there it's a move to the gap. */
const SLOT_EDGE = 14;
/** While a gap is aimed the ghost hangs this far under the pointer, and never nearer than this under the line by it,
 *  so the line (drawn over the ghost) doesn't cross its name. */
const GHOST_HANG = 10;
const GHOST_UNDER_LINE = 6;

/** What a drag points at to split: a row (a lone one, or one in a group) or a group's header (`key`), the row it would
 *  join (`lead`), and whether that one already has 4 panes; or (`out`) the list outside every group, for a row dragged
 *  out of its own. */
type Aim = { key: string; lead: number; full: boolean; out?: boolean };

/** Where a drag would move its row or group to: the gap before the row or group led by `before` (null: after the
 *  last). The line shows on `on`'s `edge`, at `y` in the window. */
type Slot = { before: number | null; on: number; edge: 'before' | 'after'; y: number };
const sameSlot = (a: Slot | null, b: Slot | null) => a?.before === b?.before && a?.on === b?.on && a?.edge === b?.edge;

/**
 * Dragging a row in the list (Arc's): a row of its own, one of a group's rows, or a group by its header. Past 4px it
 * dims and a ghost of it (its name on a chip) follows the pointer.
 *
 * To a gap between the rows and groups it moves there: a line shows in the nearest gap at once, and let go it's
 * placed there (ui/rowOrder.ts), a group with everything in it. A pinned one stays among the pinned, the others under
 * them: the line stops at that edge. A group's row let go on a gap leaves its group and lands there.
 *
 * Resting on another row, a group's row or a group's header for ARM_DELAY_MS (off their top and bottom edges, which
 * are the gaps') arms a split instead: that row warms to the accent, says "Release to split", the line goes and a
 * dock shows where the session will sit, a pane on the group's tree. Let go armed and it joins that row's group as a
 * new pane (tabs.ts joinRow). A group's header only moves: it never splits.
 *
 * A group's row resting anywhere in the sidebar outside every row and group arms the other way: the ghost says
 * "Release to unsplit", and let go it's a row of its own again (tabs.ts leave); over its own group nothing arms. Let
 * go anywhere else, Esc, or the window losing focus, and nothing changes: the ghost slides home. Mouse and pen on the
 * desktop only: a finger scrolls the list.
 */
function useRowDrag(list: RefObject<HTMLUListElement | null>, tabs: TabsState, compact: boolean, join: (id: number, lead: number) => void, unsplit: (id: number) => void, move: (ids: number[], before: number | null) => void) {
  type Kind = 'row' | 'group';
  const [view, setView] = useState<{ sid: number; name: string; kind: Kind; from: number | null; aim: Aim | null; armed: boolean; slot: Slot | null } | null>(null);
  const [chip, setChip] = useState<{ n: number; name: string; kind: Kind } | null>(null);
  const chipRef = useRef<HTMLDivElement>(null);
  /** `sid`: the row's session, or the group's lead. `from`: the lead of the group the row is dragged out of (null: a
   *  row or a group of its own). */
  type Drag = { sid: number; name: string; kind: Kind; from: number | null; pointer: number; x0: number; y0: number; x: number; y: number; live: boolean; aim: Aim | null; armed: boolean; slot: Slot | null; timer: number; n: number };
  const drag = useRef<Drag | null>(null);
  const seq = useRef(0);
  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;

  const place = () => {
    const d = drag.current;
    const g = chipRef.current;
    if (!d || !g) return;
    g.style.left = `${d.x}px`;
    // a gap aimed (and no split armed): its top edge under the pointer, and under the line when that's just below
    const near = d.slot && !d.armed && d.slot.y - d.y <= SLOT_REACH ? d.slot.y + GHOST_UNDER_LINE : 0;
    g.style.top = `${d.slot && !d.armed ? Math.max(d.y + GHOST_HANG, near) : d.y}px`;
  };
  // the chip just drawn: under the pointer before it paints
  useLayoutEffect(place, [chip]);

  /** The list's rows and groups, top to bottom (not a dock, nor one that's leaving), each by its lead. */
  const tops = () =>
    [...(list.current?.children ?? [])].flatMap((el) => {
      if (!(el instanceof HTMLElement) || el.classList.contains('ws-sb-dock') || el.dataset.phase === 'leave') return [];
      const lead = Number(el.classList.contains('ws-sb-group') ? el.dataset.lead : el.dataset.sessionId);
      return Number.isFinite(lead) ? [{ el, lead }] : [];
    });

  /** The gap it would move to: the nearest one to the pointer over a row, near the top or bottom of a group, or the
   *  end under the last. Kept among its own (pinned or not). Null where it already is, and outside the list. */
  const slotAt = (d: Drag, x: number, y: number): Slot | null => {
    const box = list.current?.closest('.ws-sb-scroll')?.getBoundingClientRect();
    if (!box || x < box.left || x > box.right || y < box.top || y > box.bottom) return null;
    const items = tops();
    const n = items.length;
    if (!n) return null;
    let k = -1;
    for (let i = 0; i < n && k < 0; i++) {
      const r = items[i].el.getBoundingClientRect();
      if (y < r.top) k = i;
      else if (y <= r.bottom) {
        // over its own group nothing happens, as nothing arms there
        if (d.from !== null && items[i].lead === d.from) return null;
        // a group being moved takes the nearer gap wherever it is over one
        const reach = d.kind === 'group' ? r.height / 2 : Math.min(r.height / 2, SLOT_REACH);
        if (y - r.top <= reach) k = i;
        else if (r.bottom - y < reach) k = i + 1;
        else return null;
      }
    }
    if (k < 0) {
      // under the last one: the end (a group's row there leaves its group instead: out)
      if (d.from !== null) return null;
      k = n;
    }
    const pinned = items.filter((t) => isPinned(t.lead)).length;
    k = isPinned(d.sid) ? Math.min(k, pinned) : Math.max(k, pinned);
    const self = d.from === null ? items.findIndex((t) => t.lead === d.sid) : -1;
    if (self >= 0 && (k === self || k === self + 1)) return null;
    return k < n
      ? { before: items[k].lead, on: items[k].lead, edge: 'before', y: items[k].el.getBoundingClientRect().top }
      : { before: null, on: items[n - 1].lead, edge: 'after', y: items[n - 1].el.getBoundingClientRect().bottom };
  };

  /** What's under the pointer to split with: a row or a header of this list that isn't the dragged one or in its
   *  group, off its top and bottom edges; the dock keeps the aim. For a group's row, the sidebar outside every row and
   *  group (the Sessions header, the space under the list) is out: that's where a row leaves its group. */
  const aimAt = (d: Drag, x: number, y: number): Aim | null | 'keep' => {
    const hit = document.elementFromPoint(x, y);
    const out: Aim | null = d.from === null ? null : { key: 'out', lead: d.from, full: false, out: true };
    if (!hit || !list.current) return null;
    if (!list.current.contains(hit)) return list.current.closest('.ws-sb')?.contains(hit) ? out : null;
    if (hit.closest('.ws-sb-dock')) return 'keep';
    if (d.kind === 'group') return null;
    const row = hit.closest<HTMLElement>('.ws-sb-row');
    const group = hit.closest<HTMLElement>('.ws-sb-group');
    const head = hit.closest('.ws-sb-ghead');
    if (d.from !== null && Number(group?.dataset.lead) === d.from) return null;
    if (!row && !head) return group ? null : out;
    if (hit.closest('[data-phase=leave]') || Number(row?.dataset.sessionId) === d.sid) return null;
    const lead = Number(group?.dataset.lead ?? row?.dataset.sessionId);
    if (!Number.isFinite(lead)) return null;
    const top = (group ?? row)!.getBoundingClientRect();
    const edge = Math.min(SLOT_EDGE, top.height * 0.3);
    if (y - top.top < edge || top.bottom - y < edge) return null;
    const panes = groupOf(tabsRef.current, lead)?.panes.length ?? 1;
    return { key: head ? `h${lead}` : `r${row!.dataset.sessionId}`, lead, full: panes >= MAX_PANES };
  };

  const aimTo = (d: Drag, aim: Aim | null) => {
    clearTimeout(d.timer);
    d.aim = aim;
    d.armed = false;
    setView((v) => v && { ...v, aim, armed: false });
    if (!aim || aim.full) return;
    d.timer = window.setTimeout(() => {
      if (drag.current !== d || d.aim !== aim) return;
      d.armed = true;
      setView((v) => v && { ...v, armed: true });
      place();
    }, ARM_DELAY_MS);
  };

  const onMove = (e: globalThis.PointerEvent) => {
    const d = drag.current;
    if (!d || e.pointerId !== d.pointer) return;
    d.x = e.clientX;
    d.y = e.clientY;
    if (!d.live) {
      if (Math.hypot(d.x - d.x0, d.y - d.y0) < DRAG_SLOP) return;
      d.live = true;
      document.documentElement.dataset.rowDrag = '';
      window.getSelection()?.removeAllRanges();
      setView({ sid: d.sid, name: d.name, kind: d.kind, from: d.from, aim: null, armed: false, slot: null });
      setChip({ n: d.n, name: d.name, kind: d.kind });
    }
    e.preventDefault();
    const at = aimAt(d, d.x, d.y);
    if (at === 'keep') return place();
    const slot = slotAt(d, d.x, d.y);
    // in a gap it lands there: that isn't the sidebar outside every row
    const aim = slot && at?.out ? null : at;
    if ((aim?.key ?? null) !== (d.aim?.key ?? null)) aimTo(d, aim);
    const same = sameSlot(slot, d.slot);
    d.slot = slot;
    if (!same) setView((v) => v && { ...v, slot });
    place();
  };

  const end = (commit: boolean) => {
    const d = drag.current;
    if (!d) return;
    drag.current = null;
    clearTimeout(d.timer);
    window.removeEventListener('pointermove', win.current.move);
    window.removeEventListener('pointerup', win.current.up);
    window.removeEventListener('pointercancel', win.current.cancel);
    window.removeEventListener('keydown', win.current.key, true);
    window.removeEventListener('blur', win.current.cancel);
    if (!d.live) return;
    delete document.documentElement.dataset.rowDrag;
    // the release after a drag isn't a click on the row under it (now, or when the button goes up after Esc)
    const stop = (c: MouseEvent) => {
      c.stopPropagation();
      c.preventDefault();
    };
    window.addEventListener('click', stop, { capture: true, once: true });
    const off = () => setTimeout(() => window.removeEventListener('click', stop, true), 0);
    if (commit) off();
    else window.addEventListener('pointerup', off, { once: true });
    setView(null);
    if (commit && d.armed && d.aim) {
      setChip(null);
      if (d.aim.out) unsplit(d.sid);
      else join(d.sid, d.aim.lead);
      return;
    }
    if (commit && d.slot) {
      setChip(null);
      // a group goes with everything in it, so a session that leaves it later is a row beside it
      const g = d.from === null ? groupOf(tabsRef.current, d.sid) : null;
      if (d.from !== null) unsplit(d.sid);
      move(g ? [d.sid, ...members(g).filter((id) => id !== d.sid)] : [d.sid], d.slot.before);
      return;
    }
    // nothing changes: the ghost slides back to its row and fades
    const g = chipRef.current;
    const home = list.current?.querySelector(d.kind === 'group' ? `.ws-sb-group[data-lead="${d.sid}"] .ws-sb-ghead` : `.ws-sb-row[data-session-id="${d.sid}"]`)?.getBoundingClientRect();
    if (g && home) {
      g.dataset.back = '';
      g.style.left = `${home.left + 16}px`;
      g.style.top = `${home.top + home.height / 2}px`;
    }
    setTimeout(() => setChip((c) => (c?.n === d.n ? null : c)), g && home ? GHOST_BACK_MS : 0);
  };

  const onKey = (e: globalThis.KeyboardEvent) => {
    if (e.key !== 'Escape' || !drag.current) return;
    e.preventDefault();
    e.stopPropagation();
    end(false);
  };

  // Stable listeners on the window (added on press, removed at the end) reaching this render's handlers.
  const latest = useRef({ onMove, end, onKey });
  latest.current = { onMove, end, onKey };
  const win = useRef({
    move: (e: globalThis.PointerEvent) => latest.current.onMove(e),
    up: (e: globalThis.PointerEvent) => e.pointerId === drag.current?.pointer && latest.current.end(true),
    cancel: () => latest.current.end(false),
    key: (e: globalThis.KeyboardEvent) => latest.current.onKey(e),
  });
  useEffect(() => () => latest.current.end(false), []);

  /** A press on a row (`from`: its group's lead, when it's in one) or on a group's header (`kind` group; `s` its lead). */
  const lift = (e: ReactPointerEvent<HTMLElement>, s: Session, from: number | null = null, kind: Kind = 'row') => {
    if (e.button !== 0 || e.pointerType === 'touch' || compact || drag.current || e.currentTarget.dataset.mode) return;
    // its actions, its End confirm and its Rename dialog stay theirs
    if ((e.target as Element).closest('.ws-sb-acts, .ws-sb-ask, dialog')) return;
    const n = ++seq.current;
    drag.current = { sid: s.id, name: s.name, kind, from, pointer: e.pointerId, x0: e.clientX, y0: e.clientY, x: e.clientX, y: e.clientY, live: false, aim: null, armed: false, slot: null, timer: 0, n };
    window.addEventListener('pointermove', win.current.move);
    window.addEventListener('pointerup', win.current.up);
    window.addEventListener('pointercancel', win.current.cancel);
    window.addEventListener('keydown', win.current.key, true);
    window.addEventListener('blur', win.current.cancel);
  };

  // a group's row leaves it either way: out of every row, or onto a gap
  const leaves = !!view && (view.armed ? !!view.aim?.out : view.from !== null && !!view.slot);
  const ghost =
    chip &&
    createPortal(
      <div ref={chipRef} key={chip.n} className="ws-sb-ghost" data-armed={view?.armed && !view.aim?.out ? '' : undefined} data-out={leaves ? '' : undefined} data-slot={view?.slot && !view.armed ? '' : undefined} data-testid="row-ghost" aria-hidden="true">
        <Ic icon={chip.kind === 'group' ? IconFolder : IconTerminal2} size={14} />
        <span className="ws-sb-ghost-text">
          <span>{chip.name}</span>
          <span className="ws-sb-ghost-hint" data-testid="ghost-hint">
            Release to unsplit
          </span>
        </span>
      </div>,
      document.body,
    );
  return { view, lift, ghost };
}

/** Where the dragged session will sit once let go (armed): a pane on the tree, after the group's last row (or under a
 *  lone row, its leg hanging from that row's mark), dashed in the accent, with the split it makes and its cell in it.
 *  Not a control: the pointer resting on it keeps the row it's aimed at. */
function SplitDock({ name, layout, pane, lone }: { name: string; layout: Layout; pane: number; lone?: boolean }) {
  const n = paneOrder(layout).length;
  return (
    <li className="ws-sb-row ws-sb-dock" data-testid="split-dock" aria-hidden="true">
      <Leg first={!!lone} last from={lone ? -30 : -7} />
      <div className="ws-sb-body">
        <div className="ws-sb-main">
          <span className="ws-sb-line">
            <Mark tone="run" />
            <span className="ws-sb-title">{name}</span>
            <LayoutIcon layout={layout} on={pane} />
          </span>
          <span className="ws-sb-sub" data-testid="split-dock-sub">
            Pane {n} of {n}
          </span>
        </div>
      </div>
    </li>
  );
}

/** A split's panes in a 16×14 window outline: the dividers between them, and pane `on`'s cell filled in the accent. */
function LayoutIcon({ layout, on }: { layout: Layout; on: number }) {
  const r = (v: number) => Math.round(v * 2) / 2;
  let cell: { x: number; y: number; w: number; h: number } | null = null;
  let d = '';
  const walk = (l: Layout, x: number, y: number, w: number, h: number) => {
    if ('pane' in l) {
      if (l.pane === on) cell = { x, y, w, h };
      return;
    }
    let at = 0;
    l.kids.forEach((k, i) => {
      const s = l.sizes[i];
      if (l.dir === 'row') {
        const kx = r(x + at * w);
        if (i) d += `M${kx} ${y}V${y + h}`;
        walk(k, kx, y, r(x + (at + s) * w) - kx, h);
      } else {
        const ky = r(y + at * h);
        if (i) d += `M${x} ${ky}H${x + w}`;
        walk(k, x, ky, w, r(y + (at + s) * h) - ky);
      }
      at += s;
    });
  };
  walk(layout, 2, 2, 12, 10);
  const c = cell as { x: number; y: number; w: number; h: number } | null;
  return (
    <svg className="ws-sb-lay" width="16" height="14" viewBox="0 0 16 14" aria-hidden="true">
      {c && <rect className="ws-sb-lay-on" x={c.x} y={c.y} width={c.w} height={c.h} />}
      <rect className="ws-sb-lay-line" x="1.5" y="1.5" width="13" height="11" rx="2.5" />
      {d && <path className="ws-sb-lay-line" d={d} />}
    </svg>
  );
}

/** A group's rows have no ⌘1–9 of their own: the group's header has it. */
const NO_KEYS = new Map<number, number>();

/** A workspace group's header: its folder and name (the lead session's, as the title bar says it). */
function GroupHead({ lead, index, current, armed, onLift }: { lead: Session; index: number; current: boolean; armed?: boolean; onLift?: (e: ReactPointerEvent<HTMLElement>) => void }) {
  const a = useActions();
  const [editing, setEditing] = useState(false);
  const open = useRef<HTMLButtonElement>(null);
  const tip = lead.name;
  return (
    <div className="ws-sb-ghead" data-split="" data-current={current ? '' : undefined} data-drag={armed ? 'armed' : undefined} onPointerDown={onLift}>
      <button ref={open} type="button" className="ws-sb-gopen ws-sb-wopen" aria-current={current ? 'true' : undefined} title={tip} data-testid="split-open" onClick={() => a.selectSession(lead.id)} onDoubleClick={() => setEditing(true)}>
        <Ic icon={IconFolder} size={14} />
        <span className="ws-sb-glabel">{lead.name}</span>
        {index < 9 && (
          <span className="ws-sb-key" aria-hidden="true">
            {numModLabel()}
            {index + 1}
          </span>
        )}
      </button>
      <RenameDialog
        open={editing}
        name={lead.name}
        automatic={lead.nameSource === 'user'}
        onSave={(name) => void a.rename(lead.id, name)}
        onClose={() => {
          open.current?.focus();
          setEditing(false);
        }}
      />
    </div>
  );
}

/** Groups by program (a header with the program's mark) or by folder (a header that folds), the rows on tree legs under either; the groups glide too. */
function SessionGroups({ groups, index, organize, current }: { groups: SessionGroup[]; index: Map<number, number>; organize: Organize; current?: number }) {
  const { ref, shown } = useListMotion<SessionGroup, HTMLUListElement>(groups, (g) => g.key || '-');
  const [folded, setFolded] = useState<Set<string>>(new Set());
  const tree = organize === 'folder';
  return (
    <ul className="ws-sb-list" ref={ref} data-testid="session-list" data-organize={organize}>
      {shown.map(({ item: g, key, phase }) => {
        const shut = tree && folded.has(key);
        const head = (
          <>
            {tree ? (
              <span className="ws-sb-chev" data-folded={shut ? '' : undefined}>
                <Ic icon={IconChevronDown} size={14} />
              </span>
            ) : (
              <span className="ws-sb-gicon">
                <ProgramIcon program={g.program} />
              </span>
            )}
            <span className="ws-sb-glabel">{g.label || 'Other'}</span>
            {!tree && <Mono>{g.sessions.length}</Mono>}
          </>
        );
        return (
          <li key={key} data-key={key} className="ws-sb-group" data-phase={rowPhase(phase)} data-testid="session-group" data-group={g.label}>
            {tree ? (
              <button
                type="button"
                className="ws-sb-ghead"
                aria-expanded={!shut}
                title={g.label}
                onClick={() =>
                  setFolded((f) => {
                    const n = new Set(f);
                    if (n.has(key)) n.delete(key);
                    else n.add(key);
                    return n;
                  })
                }
              >
                {head}
              </button>
            ) : (
              <div className="ws-sb-ghead" title={g.label}>
                {head}
              </div>
            )}
            {!shut && <SessionRows sessions={g.sessions} index={index} organize={organize} current={current} tree />}
          </li>
        );
      })}
    </ul>
  );
}

// Regrouping lays the whole list out again: each row glides from where it was to where it lands (its text
// sideways too, as the tree indents it), and what's new (headers, tree legs, rows a fold had hidden) fades in. It runs
// when the grouping changes (the menu snaps the rows first) and when a workspace gathers or lets go of sessions.
const REGROUP_MS = 280;
const REGROUP_EASE = 'cubic-bezier(0.23, 1, 0.32, 1)'; // --ease-out
type RowPlace = { top: number; left: number; leg: boolean };
let regroupFrom: { at: number; rows: Map<string, RowPlace> } | null = null;

/** Every row's place in the list (its top and its text's left, in the scrolled content), and whether it has a leg. */
function rowPlaces(list: Element | null) {
  const rows = new Map<string, RowPlace>();
  if (!list) return rows;
  const box = list.getBoundingClientRect();
  list.querySelectorAll<HTMLElement>('.ws-sb-row[data-key]').forEach((r) => {
    const body = r.querySelector('.ws-sb-body');
    if (body) rows.set(r.dataset.key!, { top: r.getBoundingClientRect().top - box.top + list.scrollTop, left: body.getBoundingClientRect().left - box.left, leg: !!r.querySelector(':scope > .ws-sb-leg') });
  });
  return rows;
}

/** Where every row is now, just before the grouping changes. */
function snapRows(from: Element | null) {
  regroupFrom = { at: performance.now(), rows: rowPlaces(from?.closest('.ws-sb')?.querySelector('.ws-sb-scroll') ?? null) };
}

/** After the grouping changed (`shape`: the organize setting and which sessions each workspace holds): the rows glide
 *  from where they were. */
function useRegroupGlide(shape: string, scroll: RefObject<HTMLDivElement | null>) {
  const last = useRef<{ shape: string; rows: Map<string, RowPlace> } | null>(null);
  useLayoutEffect(() => {
    const list = scroll.current;
    const prev = last.current;
    const snap = regroupFrom;
    regroupFrom = null;
    // a snap from the menu, else the layout before the change; a change from another device (a stale snap) just shows
    const from = prev && prev.shape !== shape ? (snap && performance.now() - snap.at < 2000 ? snap.rows : prev.rows) : null;
    if (from && list && !reducedMotion()) glide(list, from);
    last.current = { shape, rows: rowPlaces(list) };
  });
}

function glide(list: HTMLElement, from: Map<string, RowPlace>) {
  const now = rowPlaces(list);
  const moving = { duration: REGROUP_MS, easing: REGROUP_EASE };
  const fade = { duration: 200, delay: 80, easing: REGROUP_EASE, fill: 'backwards' as const };
  list.querySelectorAll<HTMLElement>('.ws-sb-row[data-key]').forEach((r) => {
    const was = from.get(r.dataset.key!);
    const is = now.get(r.dataset.key!);
    const body = r.querySelector<HTMLElement>('.ws-sb-body');
    if (!was || !is || !body) {
      r.animate([{ opacity: 0 }, { opacity: 1 }], fade);
      return;
    }
    // a leg it already had stays put; a new one fades in
    if (!was.leg) r.querySelector<SVGElement>(':scope > .ws-sb-leg')?.animate([{ opacity: 0 }, { opacity: 1 }], fade);
    const dy = was.top - is.top;
    const dx = was.left - is.left;
    if (dy) r.animate([{ transform: `translateY(${dy}px)` }, { transform: 'none' }], moving);
    if (dx) body.animate([{ transform: `translateX(${dx}px)` }, { transform: 'none' }], moving);
  });
  list.querySelectorAll<HTMLElement>('.ws-sb-ghead').forEach((h) => h.animate([{ opacity: 0 }, { opacity: 1 }], fade));
}

const ORGANIZE: { value: Organize; label: string }[] = [
  { value: 'none', label: 'Don’t group' },
  { value: 'folder', label: 'By folder' },
  { value: 'program', label: 'By program' },
];

/** The Sessions header's menu: group sessions by folder or by program (saved to the account). */
function OrganizeMenu({ value }: { value: Organize }) {
  const m = useMenu();
  const on = value !== 'none';
  const btn = (
    <Button
      variant="icon"
      size="xs"
      radius={6}
      aria-label="Organize sessions"
      aria-haspopup="menu"
      aria-expanded={m.open}
      aria-pressed={on}
      data-testid="organize"
      onClick={() => m.setOpen((o) => !o)}
    >
      <Ic icon={IconFilter2} size={14} />
    </Button>
  );
  return (
    <span className="ws-menu-wrap" ref={m.ref}>
      {m.open ? (
        btn
      ) : (
        <Tooltip label="Organize sessions" sub={on ? (value === 'folder' ? 'by folder' : 'by program') : undefined} align="end">
          {btn}
        </Tooltip>
      )}
      {m.p.mounted && (
        <div className="ws-menu ds-float" role="menu" aria-label="Organize sessions" {...presence(m.p)}>
          <div className="ws-menu-label">
            <Pix>Group sessions</Pix>
          </div>
          {ORGANIZE.map((o) => (
            <button
              key={o.value}
              type="button"
              className="ws-menu-item"
              role="menuitemradio"
              aria-checked={value === o.value}
              data-testid={`organize-${o.value}`}
              onClick={(e) => {
                if (o.value !== value) snapRows(e.currentTarget);
                setOrganize(o.value);
                m.setOpen(false);
              }}
            >
              <span className="ws-menu-check">{value === o.value && <Ic icon={IconCheck} size={14} stroke={2} />}</span>
              {o.label}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

/** The machine's pages, under the wordmark and over the sessions: each takes the place of the panes (the row's panes wait behind it), and
 *  the one shown is the current item. */
function PageMenu({ machine }: { machine: string }) {
  const a = useActions();
  const page = a.tabs.page;
  const desktopRunning = useStore((s) => s.desktopRunning);
  const item = (p: 'desktop' | 'files' | 'settings', icon: TablerIcon, label: string, title: string, open: () => void, live?: boolean) => (
    <li>
      <button type="button" className="ws-sb-page" aria-current={page === p ? 'page' : undefined} title={title} data-testid={`open-${p}`} onClick={open}>
        <Ic icon={icon} size={16} />
        <span className="ws-grow">{label}</span>
        {live && <span className="ws-sb-page-live" role="img" aria-label="running" />}
      </button>
    </li>
  );
  return (
    <ul className="ws-sb-pages" aria-label="Machine">
      {item('desktop', IconDeviceDesktop, 'Desktop', `${machine}’s screen`, a.openDesktop, desktopRunning)}
      {item('files', IconFolder, 'Files', `${machine}’s home`, a.openFiles)}
      {item('settings', IconSettings, 'Settings', 'Settings', a.openSettings)}
    </ul>
  );
}

/** You, at the foot: a menu with the dashboard, the guide and signing out. */
function AccountMenu({ email, where, drawer }: { email: string; where: string; drawer: boolean }) {
  const a = useActions();
  const m = useMenu();
  const item = (icon: TablerIcon) => (
    <span className="ws-menu-icon">
      <Ic icon={icon} size={16} />
    </span>
  );
  return (
    <span className="ws-menu-wrap ws-sb-foot" ref={m.ref} data-open={m.open ? '' : undefined}>
      <button type="button" className="ws-sb-me-btn" aria-haspopup="menu" aria-expanded={m.open} title={email} data-testid="sidebar-user" onClick={() => m.setOpen((o) => !o)}>
        <span className="ws-avatar" aria-hidden="true">
          {(email || '?').slice(0, 1).toUpperCase()}
        </span>
        <span className="ws-sb-email">{email}</span>
      </button>
      {!drawer && (
        <button
          type="button"
          className="ws-sb-kbd"
          aria-label="Command palette"
          onClick={() => {
            m.setOpen(false);
            a.openPalette();
          }}
        >
          <Kbd k="K" mod onSurface />
        </button>
      )}
      {m.p.mounted && (
        <div className="ws-menu ds-float ws-acct" role="menu" aria-label="Account" data-up {...presence(m.p)}>
          <div className="ws-acct-who">
            <span className="ws-acct-email">{email}</span>
            {where && <span className="ws-acct-where">{where}</span>}
          </div>
          <div className="ws-menu-rule" data-head role="separator" />
          <a className="ws-menu-item" role="menuitem" href="/dashboard" data-testid="sidebar-dashboard">
            {item(IconLayoutDashboard)}
            <span className="ws-grow">Dashboard</span>
          </a>
          <a className="ws-menu-item" role="menuitem" href="/guide" target="_blank" rel="noopener" data-testid="sidebar-guide">
            {item(IconBook)}
            <span className="ws-grow">Guide</span>
            <span className="ws-menu-out" aria-label="opens in a new tab">
              <Ic icon={IconArrowUpRight} size={14} />
            </span>
          </a>
          <a className="ws-menu-item" role="menuitem" href="/guide#download" target="_blank" rel="noopener" data-testid="sidebar-apps">
            {item(IconDownload)}
            <span className="ws-grow">Get the apps</span>
            <span className="ws-menu-out" aria-label="opens in a new tab">
              <Ic icon={IconArrowUpRight} size={14} />
            </span>
          </a>
          <div className="ws-menu-rule" role="separator" />
          <button type="button" className="ws-menu-item" role="menuitem" data-danger="" data-testid="logout" onClick={() => void a.logout()}>
            {item(IconLogout)}
            <span className="ws-grow">Sign out</span>
          </button>
        </div>
      )}
    </span>
  );
}

export function Sidebar({ variant = 'side' }: { variant?: 'side' | 'drawer' }) {
  const a = useActions();
  const drawer = variant === 'drawer';
  const sessions = useStore((s) => s.sessions);
  const loaded = useStore((s) => s.sessionsLoaded);
  const me = useStore((s) => s.me);
  const machine = me?.machine?.id ?? 'the cloud machine';
  // A phone's Home groups as the account says; the desktop sidebar isn't grouped, so rows keep their places as in
  // other terminals' tab lists (spec "The sessions list").
  const accountOrganize = useOrganize();
  const organize: Organize = drawer ? accountOrganize : 'none';
  // the list holds still while the pointer or a finger is on it, and moves after
  const [holding, setHolding] = useState(false);
  // the rows keep their places: opening, switching or splitting never moves one
  const ordered = useListOrder(sessions, holding);
  const pins = usePins();
  const order = useOrder();
  // the desktop's rows are workspaces: a session another row holds has none of its own (a phone lists them all)
  const held = drawer ? null : inner(a.tabs);
  const groups = pinnedFirst(groupSessions(held ? ordered.filter((s) => !held.has(s.id)) : ordered, organize));
  const scroll = useRef<HTMLDivElement>(null);
  // which sessions each workspace holds: a change regroups the rows, and they glide
  useRegroupGlide(`${organize}|${held ? a.tabs.groups.map((g) => members(g).join(',')).join(';') : ''}`, scroll);
  // ⌘1–9 follow the order shown
  const index = new Map(groups.flatMap((g) => g.sessions).map((s, i) => [s.id, i]));
  // the row whose panes show (none under a page); a phone's, the session on screen
  const shown = shownGroup(a.tabs);
  const cur = drawer ? (focusedSession(a.tabs) ?? undefined) : a.tabs.page ? undefined : shown?.lead;
  const statusOf = held ? (s: Session) => rowStatus(s, sessions, a.tabs) : undefined;
  useKbdHeld();
  // pins of sessions that are gone go
  useEffect(() => {
    if (loaded) prunePins(sessions.map((s) => s.id));
  }, [loaded, sessions, pins]);
  useEffect(() => {
    if (loaded) pruneOrder(sessions.map((s) => s.id));
  }, [loaded, sessions, order]);

  return (
    <nav className="ws-sb" data-drawer={drawer ? '' : undefined} aria-label="Sessions" data-testid="sidebar">
      <div className="ws-sb-top">
        <span className="ws-wordmark">cmx_</span>
        <span className="ws-grow" />
        {/* the phone drawer has no close button on the canvas: the scrim, Esc or picking a session closes it */}
        {!drawer && <Tool icon={IconLayoutSidebarLeftCollapse} label="Hide sidebar" held k={isMac() ? 'B' : undefined} align="end" onClick={a.toggleSidebar} />}
      </div>
      {/* a phone's Home: the load banner under the top row (the panes have it on a wider screen) */}
      {drawer && <LoadBanner />}

      <PageMenu machine={machine} />

      <div className="ws-sb-head">
        <span className="ws-sb-count">
          <Mono className="ws-sb-head-word">Sessions</Mono>
          <Mono>{sessions.length}</Mono>
        </span>
        {drawer && <OrganizeMenu value={organize} />}
        <Tool icon={IconPlus} iconSize={14} size="xs" label="New session" align="end" data-testid="new-session" onClick={() => a.startNew()} />
      </div>

      <div className="ws-sb-scroll" ref={scroll} onPointerEnter={() => setHolding(true)} onPointerLeave={() => setHolding(false)}>
        {organize === 'none' ? (
          held ? (
            <WorkspaceRows sessions={groups[0]?.sessions ?? []} index={index} current={cur} focused={focusedSession(a.tabs) ?? undefined} statusOf={statusOf} />
          ) : (
            <SessionRows sessions={groups[0]?.sessions ?? []} index={index} organize="none" current={cur} testid="session-list" statusOf={statusOf} />
          )
        ) : (
          <SessionGroups key={organize} groups={groups} index={index} organize={organize} current={cur} />
        )}
        {loaded && !sessions.length && <Empty size="sm" className="ws-sb-empty" lead={`Nothing running on ${machine} yet.`} />}
      </div>

      <AccountMenu email={me?.email ?? ''} where={[me?.machine?.id, regionCity(me?.machine?.region)].filter(Boolean).join(' · ')} drawer={drawer} />
    </nav>
  );
}
